/**
 * `{online, pending, rejected, lastSyncAt}`, reactive: the offline banner
 * and the guidance screen's disabled "Suivant" both read this
 * (`docs/OFFLINE.md` §2).
 */
import { useCallback, useEffect, useState } from 'react';

import { getLastSyncAt, list, queueEvents } from './queue';

import type { QueueEntry } from './queue';

/** Snapshot of the offline queue, refreshed on every relevant event. */
export interface OfflineStatus {
  /** `navigator.onLine`, kept in sync with the `online`/`offline` events. */
  online: boolean;
  /** Entries still waiting to be sent (`pending` or currently `sending`). */
  pending: number;
  /** Entries the server has finally refused (`4xx` other than `429`). */
  rejected: QueueEntry[];
  /** ISO timestamp of the last successful replay, or `null`. */
  lastSyncAt: string | null;
}

/**
 * Read the offline queue's live status.
 *
 * @returns The current `{online, pending, rejected, lastSyncAt}` snapshot.
 */
export function useOfflineStatus(): OfflineStatus {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [rejected, setRejected] = useState<QueueEntry[]>([]);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const entries = await list();
    setPending(
      entries.filter((entry) => entry.status === 'pending' || entry.status === 'sending').length,
    );
    setRejected(entries.filter((entry) => entry.status === 'rejected'));
    setLastSyncAt(await getLastSyncAt());
  }, []);

  useEffect(() => {
    void refresh();

    const onOnline = () => {
      setOnline(true);
      void refresh();
    };
    const onOffline = () => setOnline(false);
    const onChange = () => void refresh();

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    queueEvents.addEventListener('change', onChange);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      queueEvents.removeEventListener('change', onChange);
    };
  }, [refresh]);

  return { online, pending, rejected, lastSyncAt };
}
